import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Component from '../../components/Component';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { getPredictionQuestions, patchPredictionQuestions } from '../../services/predictionQuestionsApi';

const isSuccess = (res) => String(res?.statusCode || '') === '10000' || res?.statusCode == null;

const getPayload = (res) => {
  if (res?.data && typeof res.data === 'object' && !Array.isArray(res.data)) return res.data;
  if (res && typeof res === 'object' && !Array.isArray(res)) return res;
  return {};
};

const bySortOrder = (a, b) => {
  const sa = Number(a?.sort_order ?? 1e9);
  const sb = Number(b?.sort_order ?? 1e9);
  if (sa !== sb) return sa - sb;
  return String(a?.title || '').localeCompare(String(b?.title || ''));
};

function PredictionQuestions() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialMid = searchParams.get('match_id') || '';

  const [matchIdInput, setMatchIdInput] = useState(initialMid);
  const [meta, setMeta] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [activeOrder, setActiveOrder] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const setFromConfigs = useCallback((nextConfigs) => {
    const list = Array.isArray(nextConfigs) ? [...nextConfigs] : [];
    list.sort(bySortOrder);
    setConfigs(list);
    setActiveOrder(list.filter((c) => !!c?.is_active).map((c) => c.match_prediction_question_id).filter(Boolean));
  }, []);

  const load = useCallback(
    async (midRaw) => {
      const mid = String(midRaw || '').trim();
      if (!mid) {
        showSnackbar('Enter a match id.', 'warning');
        return;
      }
      if (!/^\d+$/.test(mid) || Number(mid) <= 0) {
        showSnackbar('Match id must be a positive number.', 'warning');
        return;
      }

      setLoading(true);
      try {
        const res = await getPredictionQuestions(mid);
        if (String(res?.statusCode || '') === '10003') {
          showSnackbar('Session expired. Please login again.', 'error');
          setTimeout(() => {
            window.location = '/login';
          }, 1500);
          return;
        }
        if (!isSuccess(res)) {
          showSnackbar(res?.message || 'Failed to load prediction questions', 'error');
          return;
        }

        const payload = getPayload(res);
        setMeta({
          match_id: payload.match_id ?? mid,
          arcade_match_id: payload.arcade_match_id ?? null,
          prediction_enabled: !!payload.prediction_enabled,
        });
        setFromConfigs(payload.configs);
        setSearchParams({ match_id: String(mid) });
      } finally {
        setLoading(false);
      }
    },
    [setFromConfigs, setSearchParams, showSnackbar],
  );

  useEffect(() => {
    if (initialMid) load(initialMid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const configById = useMemo(() => {
    const m = new Map();
    configs.forEach((c) => {
      if (c?.match_prediction_question_id) m.set(c.match_prediction_question_id, c);
    });
    return m;
  }, [configs]);

  const activeSet = useMemo(() => new Set(activeOrder), [activeOrder]);

  const orderedConfigs = useMemo(() => {
    const active = activeOrder.map((id) => configById.get(id)).filter(Boolean);
    const inactive = configs
      .filter((c) => c?.match_prediction_question_id && !activeSet.has(c.match_prediction_question_id))
      .sort(bySortOrder);
    return [...active, ...inactive];
  }, [activeOrder, activeSet, configById, configs]);

  const toggleActive = (qid, checked) => {
    if (!qid) return;
    setActiveOrder((prev) => {
      const has = prev.includes(qid);
      if (checked && !has) return [...prev, qid];
      if (!checked && has) return prev.filter((id) => id !== qid);
      return prev;
    });
  };

  const moveActive = (qid, delta) => {
    setActiveOrder((prev) => {
      const idx = prev.indexOf(qid);
      if (idx < 0) return prev;
      const to = idx + delta;
      if (to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(idx, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  const resetToServer = () => setFromConfigs(configs);

  const save = async () => {
    const matchId = meta?.match_id ?? matchIdInput;
    if (!matchId) {
      showSnackbar('Match id is required', 'warning');
      return;
    }
    setSaving(true);
    try {
      const res = await patchPredictionQuestions({
        match_id: Number(matchId),
        active_match_prediction_question_ids: activeOrder,
      });
      if (String(res?.statusCode || '') === '10003') {
        showSnackbar('Session expired. Please login again.', 'error');
        setTimeout(() => {
          window.location = '/login';
        }, 1500);
        return;
      }
      if (!isSuccess(res)) {
        showSnackbar(res?.message || 'Could not save question visibility/order', 'error');
        return;
      }

      const payload = getPayload(res);
      setMeta((prev) => ({
        match_id: payload.match_id ?? prev?.match_id ?? matchId,
        arcade_match_id: payload.arcade_match_id ?? prev?.arcade_match_id ?? null,
        prediction_enabled: payload.prediction_enabled ?? prev?.prediction_enabled ?? false,
      }));
      setFromConfigs(payload.configs);
      showSnackbar('Prediction question visibility and order saved.', 'success');
    } finally {
      setSaving(false);
    }
  };

  const hasUnsavedChanges = useMemo(() => {
    const serverOrder = configs.filter((c) => !!c?.is_active).sort(bySortOrder).map((c) => c.match_prediction_question_id);
    return JSON.stringify(serverOrder) !== JSON.stringify(activeOrder);
  }, [activeOrder, configs]);

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <h3>Arcade Prediction questions</h3>
              </div>
            </div>
          </div>
        </div>

        <div className="container-fluid">
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Step 1 for prediction setup: choose active questions and their order. Step 2: open prediction candidates
              to override player options for the active player-based questions.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                label="Match ID"
                size="small"
                value={matchIdInput}
                onChange={(e) => setMatchIdInput(e.target.value)}
                sx={{ minWidth: 220 }}
              />
              <Button variant="contained" disabled={loading} onClick={() => load(matchIdInput)}>
                {loading ? <CircularProgress size={20} color="inherit" /> : 'Load'}
              </Button>
              <Button
                variant="outlined"
                component={Link}
                to={`/admin/arcade/prediction-candidates${matchIdInput ? `?match_id=${encodeURIComponent(matchIdInput)}` : ''}`}
              >
                Open Candidate Overrides
              </Button>
            </Box>
          </Paper>

          {meta ? (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip size="small" label={`Match: ${meta.match_id}`} />
                {meta.arcade_match_id ? <Chip size="small" label={`Arcade match: ${meta.arcade_match_id}`} /> : null}
                <Chip
                  size="small"
                  label={`Prediction: ${meta.prediction_enabled ? 'enabled' : 'disabled'}`}
                  color={meta.prediction_enabled ? 'success' : 'default'}
                />
                <Chip size="small" label={`Total questions: ${configs.length}`} />
                <Chip size="small" label={`Active now: ${activeOrder.length}`} color="info" />
              </Box>
            </Paper>
          ) : null}

          {meta && orderedConfigs.length === 0 ? (
            <Alert severity="info">No prediction questions found for this match.</Alert>
          ) : null}

          {orderedConfigs.map((config) => {
            const qid = config?.match_prediction_question_id;
            const isActive = activeSet.has(qid);
            const activeIndex = activeOrder.indexOf(qid);
            return (
              <Paper key={qid} sx={{ p: 2, mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="subtitle1">{config?.title || 'Untitled question'}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                      {config?.tag ? <Chip size="small" label={config.tag} /> : null}
                      {config?.category ? <Chip size="small" label={config.category} variant="outlined" /> : null}
                      <Chip size="small" label={config?.is_team_prediction ? 'Team question' : 'Player question'} />
                      {isActive ? <Chip size="small" color="success" label={`Active #${activeIndex + 1}`} /> : <Chip size="small" label="Inactive" />}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="body2">Active</Typography>
                    <Switch checked={isActive} onChange={(e) => toggleActive(qid, e.target.checked)} />
                    <Button size="small" variant="outlined" disabled={!isActive || activeIndex <= 0} onClick={() => moveActive(qid, -1)}>
                      Up
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!isActive || activeIndex < 0 || activeIndex >= activeOrder.length - 1}
                      onClick={() => moveActive(qid, 1)}
                    >
                      Down
                    </Button>
                  </Box>
                </Box>
              </Paper>
            );
          })}

          {meta ? (
            <Paper sx={{ p: 2, mt: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="contained" onClick={save} disabled={saving || !hasUnsavedChanges}>
                  {saving ? <CircularProgress size={18} color="inherit" /> : 'Save Active Questions & Order'}
                </Button>
                <Button variant="outlined" onClick={resetToServer} disabled={saving || !hasUnsavedChanges}>
                  Reset Unsaved Changes
                </Button>
                <Button
                  variant="text"
                  component={Link}
                  to={`/admin/arcade/prediction-candidates${meta?.match_id ? `?match_id=${encodeURIComponent(meta.match_id)}` : ''}`}
                >
                  Continue to Candidate Overrides
                </Button>
              </Box>
            </Paper>
          ) : null}
        </div>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Component>
  );
}

export default PredictionQuestions;
